import { Box, Button, Card, Stack, Tooltip, Typography } from '@mui/material';
import { LaunchRounded } from '@mui/icons-material';
import { Link } from 'react-router-dom';

/** Card width below which the header cannot fit a title and a full-text action side by side. */
const COMPACT_CARD_WIDTH = 480;
const CARD_CONTAINER = 'dashboard-card';

interface DashboardSectionCardProps {
    title: string;
    /** Header link, e.g. "go to the calendar". Collapses to an icon on a narrow card. */
    action?: { label: string; to: string; dataId?: string };
    /** Header control that is not a link, e.g. an "add competitor" button. Takes over from `action`. */
    actionSlot?: React.ReactNode;
    /** Smallest height of the body, so the three home cards line up. */
    minBodyHeight?: number;
    children: React.ReactNode;
}

/**
 * The card shell the three home cards share: one header scale, one padding scale, full height so a
 * row of them lines up, and a hover that lifts the border instead of the whole card.
 *
 * The header sizes off a container query rather than the viewport. These cards sit three to a row,
 * so a wide window still produces a narrow card, and a viewport breakpoint reads the wrong number.
 */
const DashboardSectionCard = ({
    title,
    action,
    actionSlot,
    minBodyHeight = 260,
    children,
}: DashboardSectionCardProps) => (
    <Card
        sx={{
            height: 1,
            display: 'flex',
            flexDirection: 'column',
            containerType: 'inline-size',
            containerName: CARD_CONTAINER,
            transition: 'border-color 0.2s ease',
            '&:hover': { borderColor: 'primary._states.outlinedBorder' },
        }}
    >
        <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            gap={1}
            sx={{ px: 2, pt: 2, pb: 1 }}
        >
            <Typography
                variant="h6"
                fontWeight={600}
                title={title}
                // Wraps rather than clips, so a translation longer than the ones measured here
                // still reads in full.
                sx={{ minWidth: 0 }}
            >
                {title}
            </Typography>
            {actionSlot ?? null}
            {!actionSlot && action && (
                <Tooltip title={action.label}>
                    <Button
                        component={Link}
                        to={action.to}
                        variant="text"
                        size="small"
                        data-id={action.dataId}
                        startIcon={<LaunchRounded />}
                        sx={{
                            flexShrink: 0,
                            whiteSpace: 'nowrap',
                            [`@container ${CARD_CONTAINER} (max-width: ${COMPACT_CARD_WIDTH}px)`]: {
                                minWidth: 0,
                                px: 1,
                                '& .MuiButton-startIcon': { mx: 0 },
                                '& .dashboard-card-action-label': { display: 'none' },
                            },
                        }}
                    >
                        <Box component="span" className="dashboard-card-action-label">
                            {action.label}
                        </Box>
                    </Button>
                </Tooltip>
            )}
        </Stack>
        <Stack sx={{ px: 2, pb: 2, flex: 1, minHeight: minBodyHeight }}>{children}</Stack>
    </Card>
);

export default DashboardSectionCard;
